import { Check, ChevronDown } from "lucide-react";

import { cn } from "../../../utils";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { type TSelectOption } from "./Select";

export type TMultiSelectProps = {
  options: TSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
};

/**
 * Accessible multi-select built on Popover + a checkbox list. Used by the grid
 * for enum filters that allow multiple values.
 */
export function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select…",
  className,
  "aria-label": ariaLabel,
}: TMultiSelectProps) {
  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onValueChange(value.filter((v) => v !== optionValue));
    } else {
      onValueChange([...value, optionValue]);
    }
  };

  const selectedLabels = options.filter((o) => value.includes(o.value)).map((o) => o.label);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-foreground ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring",
            className
          )}
        >
          <span className={cn("truncate", selectedLabels.length === 0 && "text-muted-foreground")}>
            {selectedLabels.length === 0
              ? placeholder
              : selectedLabels.length <= 2
                ? selectedLabels.join(", ")
                : `${selectedLabels.length} selected`}
          </span>
          <ChevronDown
            className="ml-2 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1">
        <ul
          role="listbox"
          aria-multiselectable
          className="max-h-64 overflow-y-auto"
        >
          {options.map((option) => {
            const checked = value.includes(option.value);
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggle(option.value)}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent focus-visible:bg-accent"
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                      checked && "bg-primary text-primary-foreground"
                    )}
                    aria-hidden
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{option.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
